'use client';

import { forwardRef, useEffect, useState } from "react";
import classes from "./home_page.module.css";
import CustomButton from "../common/buttons/button";
import { Dropdown } from "react-bootstrap";
import Grid from '@/assets/svg/icons/grid.svg';
import MapPin from '@/assets/svg/icons/map-pin.svg';
import Check from '@/assets/svg/icons/check.svg';
import ChevronDown from '@/assets/svg/icons/chevron-down.svg';
import ChevronUp from '@/assets/svg/icons/chevron-up.svg';
import FeaturedIcon from "../common/icons/featured_icon";
import CustomSVG from "../common/svg";
import TextInput from "../common/inputs/text_input";
import { getCountySeats } from "@/variables/variables";
import axios from "axios";
import { useRouter } from "next/navigation";
import { normalizeString } from "@/functions/normalize_string";
import { normalizeSearchText } from "@/functions/normalize_search_text";

const county_seats = getCountySeats();
const compareCityNames = (a = "", b = "") => a.localeCompare(b, 'hu', { sensitivity: 'base', numeric: true });

export default function WelcomeSearch({provider_types, cities}) {
    const [categorySearch, setCategorySearch] = useState("");
    const [citySearch, setCitySearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const router = useRouter();

    useEffect(() => {
        if (selectedCategory || !provider_types || provider_types.length === 0) return;

        const directMatch = provider_types.find((p) => p?._url === "helyszinek");
        if (directMatch) {
            setSelectedCategory(directMatch._url);
            return;
        }

    const nameMatch = provider_types.find((p) => normalizeSearchText(p?.name || "").includes(normalizeSearchText("helyszín")));
        if (nameMatch) setSelectedCategory(nameMatch._url);
    }, [provider_types, selectedCategory]);

    const handleClick = () => {
        if(!selectedCategory) router.push("/szolgaltatok");
        else if(!selectedCity) router.push(`/${selectedCategory}`);
        else router.push(`/${selectedCategory}/${normalizeString(selectedCity)}`);
    }

    return <div className={`${classes["welcome-search-container"]}`}>
        <WelcomeCategoryDropdown provider_types={provider_types} categorySearch={categorySearch} setCategorySearch={setCategorySearch} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
        <WelcomeCityDropdown cities={cities} citySearch={citySearch} setCitySearch={setCitySearch} selectedCity={selectedCity} setSelectedCity={setSelectedCity} />
        <CustomButton className={`${classes["welcome-button"]}`} size="sm" hierarchy={"primary"} onClick={handleClick} >Keresés</CustomButton>
    </div>
}

const CustomToggle = forwardRef(function CustomToggle({ children, onClick }, ref) {
    return (
        <div ref={ref} className="d-flex align-items-center flex-row gap-3 pointer" onClick={(e) => {e.preventDefault(); onClick(e);}}>
                {children}
        </div>
    )
});

function WelcomeCategoryDropdown({provider_types, categorySearch, setCategorySearch, selectedCategory, setSelectedCategory}) {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = () => { setIsOpen(!isOpen); }

    let filtered_providers = [];
    if(provider_types && provider_types.length > 0) filtered_providers = provider_types.filter((p) => normalizeSearchText(p.name || "").includes(normalizeSearchText(categorySearch || "")));

    let selected_category_index = -1;
    if(provider_types && provider_types.length > 0 && selectedCategory) selected_category_index = provider_types.findIndex(p => p._url === selectedCategory);

    return <Dropdown show={isOpen} onToggle={handleToggle}>
        <Dropdown.Toggle as={CustomToggle} active={isOpen} >
            <>
                <FeaturedIcon size={"xl"} color={"primary-button"} icon={Grid} />
                <div className={`${classes["welcome-dropdown-toggle"]}`}>
                    <div className="d-flex flex-row gap-1 align-items-center">
                        <p className="margin-0 color-gray-500 text-sm-regular">Kategóriák</p>
                        <CustomSVG className="stroke-gray-500" SvgFunction={isOpen ? ChevronUp : ChevronDown} height={16} width={16} />
                    </div>
                    <p className="margin-0 color-gray-900 text-lg-medium w-100 text-truncate">{selected_category_index !== -1 ? provider_types[selected_category_index].name : "Válassz kategóriát"}</p>
                </div>
            </>
        </Dropdown.Toggle>
        <Dropdown.Menu className={`${classes["welcome-dropdown-menu"]}`} >
            <div className={`${classes["welcome-dropdown-header"]}`}>
                <TextInput className={`w-100`} value={categorySearch} onChange={e => setCategorySearch(e.target.value)} type={"text"} placeholder={"Kategória"} />
            </div>
            {filtered_providers && filtered_providers.length > 0 && <>
                <div className={`${classes["welcome-dropdown-divider"]}`} />
                {filtered_providers.map(p => <div key={`welcome-provider-item-${p.id}`} className={`${classes["welcome-dropdown-item"]} pointer`} onClick={() => {setSelectedCategory(p._url); setIsOpen(false)}} >
                    <p className="margin-0 color-gray-700 text-sm-medium flex-grow-1 text-truncate">{p.name}</p>
                    {p._url === selectedCategory && <CustomSVG className="stroke-gray-700 flex-shrink-0" SvgFunction={Check} height={16} width={16} />}
                </div>)}
            </>}
        </Dropdown.Menu>
    </Dropdown>
}

function WelcomeCityDropdown({cities, citySearch, setCitySearch, selectedCity, setSelectedCity}) {
    const [isOpen, setIsOpen] = useState(false);
    const [allCities, setAllCities] = useState([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleToggle = (next) => {
        setIsOpen(next);
        if(next && !hasLoaded && !isLoading) {
            setIsLoading(true);
            axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}/region/all?page=-1`)
                .then(res => {
                    const r = res?.data?.response ?? {};
                    const cities = [
                        ...(r.cities    ?? []).map(c => ({ city: c, type: "city" })),
                        ...(r.districts ?? []).map(c => ({ city: `Budapest ${c}. kerület`, type: "district" })),
                        ...(r.counties  ?? []).map(c => ({ city: `${c} megye`, type: "county" })),
                        ...(r.regions   ?? []).map(c => ({ city: `${c} régió`, type: "region" })),
                    ];
                    setAllCities(cities);
                    setHasLoaded(true);
                })
                .catch(() => {})
                .finally(() => setIsLoading(false));
        }
    }

    let filtered_cities = [];
    const searchTerm = normalizeSearchText(citySearch || "");

    const baseCities = (cities && cities.length > 0 ? cities : county_seats).map((c) => ({
        ...c,
        type: c.type || "county_seat"
    }));

    const mergedCities = (() => {
        const map = new Map();
        const addCity = (item) => {
            if (!item || !item.city) return;
            const key = normalizeSearchText(item.city);
            if (!map.has(key)) map.set(key, item);
        };

        baseCities.forEach(addCity);
        if (allCities && allCities.length > 0) allCities.forEach(addCity);
        return Array.from(map.values());
    })();

    const cityTypePriority = {
        region: 0,
        county: 1,
        county_seat: 2,
        district: 4,
        city: 4
    };

    const source = [...mergedCities].sort((a, b) => {
        const typeA = cityTypePriority[a?.type] ?? cityTypePriority.city;
        const typeB = cityTypePriority[b?.type] ?? cityTypePriority.city;
        if (typeA !== typeB) return typeA - typeB;
        return compareCityNames(a?.city, b?.city);
    });
    if(searchTerm) {
        filtered_cities = source
            .filter((p) => normalizeSearchText(p.city).includes(searchTerm))
            .sort((a, b) => {
                const typeA = cityTypePriority[a?.type] ?? cityTypePriority.city;
                const typeB = cityTypePriority[b?.type] ?? cityTypePriority.city;
                if (typeA !== typeB) return typeA - typeB;
                return compareCityNames(a?.city, b?.city);
            });
    } else {
        filtered_cities = source;
    }

    return <Dropdown show={isOpen} onToggle={handleToggle}>
    <Dropdown.Toggle as={CustomToggle} active={isOpen} >
            <>
                <FeaturedIcon size={"xl"} color={"primary-button"} icon={MapPin} />
                <div className={`${classes["welcome-dropdown-toggle"]}`}>
                    <div className="d-flex flex-row gap-1 align-items-center">
                        <p className="margin-0 color-gray-500 text-sm-regular">Elhelyezkedés</p>
                        <CustomSVG className="stroke-gray-500" SvgFunction={isOpen ? ChevronUp : ChevronDown} height={16} width={16} />
                    </div>
                    <p className="margin-0 color-gray-900 text-lg-medium w-100 text-truncate">{selectedCity ? selectedCity : "Válassz települést"}</p>
                </div>
            </>
        </Dropdown.Toggle>
        <Dropdown.Menu className={`${classes["welcome-dropdown-menu"]}`} >
            <div className={`${classes["welcome-dropdown-header"]}`}>
                <TextInput className={`w-100`} value={citySearch} onChange={e => setCitySearch(e.target.value)} type={"text"} placeholder={"Elhelyezkedés"} />
            </div>
            {filtered_cities && filtered_cities.length > 0 && <>
                <div className={`${classes["welcome-dropdown-divider"]}`} />
                {filtered_cities.map(c => <div key={`welcome-provider-item-${c.city}`} className={`${classes["welcome-dropdown-item"]} pointer`} onClick={() => {setSelectedCity(c.city); setIsOpen(false)}} >
                    <p className="margin-0 color-gray-700 text-sm-medium flex-grow-1 text-truncate">{c.city}</p>
                    {c.city === selectedCity && <CustomSVG className="stroke-gray-700 flex-shrink-0" SvgFunction={Check} height={16} width={16} />}
                </div>)}
            </>}
            {filtered_cities && filtered_cities.length === 0 && <div className="padding-3 color-gray-500 text-sm-regular">{isLoading ? "Betöltés..." : "Nincs találat"}</div>}
        </Dropdown.Menu>
    </Dropdown>
}